import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AsyncSelect from "react-select/async";
import type { GroupBase, StylesConfig } from "react-select";
import {
  setExplorerLeadProfile,
  setExplorerLocalProfile,
  getExplorerLocalProfile,
  mapEntrySourceToExplorerFlow,
  setExplorerContext,
} from "../../explorer/explorerSession";
import ExplorerWizardShell from "./ExplorerWizardShell";
import "../css/AuthFlow.css";
import {
  CityOption,
  loadCityOptions,
} from "../../utils/cityAsyncSelectOptions";
import { getExplorerEventByCode } from "../../api/explorerOnboardingApi";
import { useDebouncedEmailExistsCheck } from "../../auth/useDebouncedEmailExistsCheck";
import { buildExplorerLoginRedirectQuery } from "../../explorer/explorerProfileSync";
import { sectors } from "../MuslimInvest/Sectors";
import { useTranslation } from "react-i18next";

const FLOW_TOTAL_STEPS = 3;
const SECTOR_OPTIONS = [...sectors].sort();

type CitySelectStyles = StylesConfig<CityOption, false, GroupBase<CityOption>>;

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/** Public route: profile step, then segmentation (before dashboard/sidebar). */
function ExplorerSignup() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { eventCode } = useParams<{ eventCode?: string }>();
  const navigate = useNavigate();

  const entrySource = useMemo(() => {
    const raw = (searchParams.get("entrySource") || "event").trim();
    return raw.length ? raw : "event";
  }, [searchParams]);

  const eventIdParam = searchParams.get("eventId");
  const [resolvedEventId, setResolvedEventId] = useState<string | null>(eventIdParam);
  const [resolvingEventCode, setResolvingEventCode] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [city, setCity] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [sector, setSector] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const { exists: emailExists, checking: emailExistsChecking, checkNow: checkEmailNow } =
    useDebouncedEmailExistsCheck(email);

  useEffect(() => {
    const flow = mapEntrySourceToExplorerFlow(entrySource);
    let cancelled = false;

    const resolveFromCode = async () => {
      if (!eventCode) {
        setResolvedEventId(eventIdParam);
        setExplorerContext(eventIdParam, flow, null);
        return;
      }

      setResolvingEventCode(true);
      try {
        const event = await getExplorerEventByCode(eventCode);
        if (cancelled) return;
        const id = String(event.id);
        setResolvedEventId(id);
        setExplorerContext(id, flow, eventCode);
      } catch {
        if (cancelled) return;
        setResolvedEventId(null);
        setExplorerContext(null, flow, eventCode);
        setError(t("explorer.signup.invalidLink"));
      } finally {
        if (!cancelled) setResolvingEventCode(false);
      }
    };

    void resolveFromCode();
    return () => {
      cancelled = true;
    };
  }, [entrySource, eventIdParam, eventCode, t]);

  /** Restore step 1 from session if user navigates back. */
  useEffect(() => {
    const saved = getExplorerLocalProfile();
    if (!saved) return;
    setFirstName(saved.firstName);
    setLastName(saved.lastName);
    setEmail(saved.email);
    if (saved.phoneNumber) setPhoneNumber(saved.phoneNumber);
    if (saved.city) setCity(saved.city);
    if (saved.jobTitle) setJobTitle(saved.jobTitle);
    if (saved.companyName) setCompanyName(saved.companyName);
    if (saved.sector) setSector(saved.sector);
    setGdprConsent(saved.gdprConsent);
    setMarketingConsent(Boolean(saved.marketingConsent));
  }, []);

  const validateProfileStep = (): boolean => {
    if (eventCode && !resolvedEventId) {
      setError(t("explorer.signup.invalidLink"));
      return false;
    }
    if (!isValidEmail(email)) {
      setError(t("explorer.signup.validEmail"));
      return false;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError(t("explorer.signup.firstLastNameRequired"));
      return false;
    }
    if (!phoneNumber.trim()) {
      setError(t("explorer.signup.phoneRequired"));
      return false;
    }
    if (!isValidPhoneNumber(phoneNumber)) {
      setError(t("explorer.signup.phoneInvalid"));
      return false;
    }
    if (!city.trim()) {
      setError(t("explorer.signup.cityRequired"));
      return false;
    }
    if (!jobTitle.trim()) {
      setError(t("explorer.signup.jobTitleRequired"));
      return false;
    }
    if (!companyName.trim()) {
      setError(t("explorer.signup.companyRequired"));
      return false;
    }
    if (!sector.trim()) {
      setError(t("explorer.signup.sectorRequired"));
      return false;
    }
    if (!gdprConsent) {
      setError(
        t("explorer.signup.gdprRequired"),
      );
      return false;
    }
    if (!marketingConsent) {
      setError(
        t("explorer.signup.marketingRequired"),
      );
      return false;
    }
    setError(null);
    return true;
  };

  const fieldInvalid = useMemo(() => {
    if (!showFieldErrors) {
      return {
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
        city: false,
        jobTitle: false,
        companyName: false,
        sector: false,
        gdprConsent: false,
        marketingConsent: false,
      };
    }
    return {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      email: !isValidEmail(email),
      phone: !phoneNumber.trim() || !isValidPhoneNumber(phoneNumber),
      city: !city.trim(),
      jobTitle: !jobTitle.trim(),
      companyName: !companyName.trim(),
      sector: !sector.trim(),
      gdprConsent: !gdprConsent,
      marketingConsent: !marketingConsent,
    };
  }, [
    showFieldErrors,
    firstName,
    lastName,
    email,
    phoneNumber,
    city,
    jobTitle,
    companyName,
    sector,
    gdprConsent,
    marketingConsent,
  ]);

  const submitProfile = async () => {
    if (eventCode && !resolvedEventId) {
      setShowFieldErrors(false);
      setError(t("explorer.signup.invalidLink"));
      return;
    }
    if (!validateProfileStep()) {
      setShowFieldErrors(true);
      return;
    }
    setShowFieldErrors(false);
    const eventIdNum = resolvedEventId ? parseInt(resolvedEventId, 10) : NaN;
    setError(null);
    if (emailExists === true) {
      return;
    }
    if (emailExistsChecking) {
      return;
    }
    setLoading(true);
    try {
      const existsResult = emailExists ?? (await checkEmailNow());
      if (existsResult) {
        return;
      }

      const profile = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim().replace(/\s/g, ""),
        city: city.trim(),
        jobTitle: jobTitle.trim(),
        companyName: companyName.trim(),
        sector: sector.trim(),
        entrySource,
        eventId: Number.isFinite(eventIdNum) ? eventIdNum : undefined,
        gdprConsent,
        marketingConsent,
      };
      setExplorerLocalProfile(profile);
      setExplorerLeadProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
      });
      navigate("/explorer/segmentation", { replace: true });
    } catch {
      setError(t("auth.registrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const goToLoginForExistingEmail = () => {
    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim().replace(/\s/g, ""),
      city: city.trim(),
      jobTitle: jobTitle.trim(),
      companyName: companyName.trim(),
      sector: sector.trim(),
      entrySource,
      gdprConsent,
      marketingConsent,
    };
    setExplorerLocalProfile(profile);
    navigate(`/?${buildExplorerLoginRedirectQuery(profile, "/explorer/signup", "login")}`, {
      replace: true,
    });
  };

  const handleNext = () => {
    void submitProfile();
  };

  const handleBack = () => {
    setError(null);
    setShowFieldErrors(false);
    navigate("/", { replace: true });
  };

  const citySelectStyles: CitySelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderColor: fieldInvalid.city
          ? "#dc3545"
          : state.isFocused
            ? "#86b7fe"
            : base.borderColor || "#ced4da",
        boxShadow: fieldInvalid.city
          ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
          : state.isFocused
            ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
            : base.boxShadow || "none",
        "&:hover": {
          borderColor: fieldInvalid.city
            ? "#dc3545"
            : state.isFocused
              ? "#86b7fe"
              : base.borderColor || "#ced4da",
        },
        borderWidth: "1px",
        borderRadius: "0.375rem",
        minHeight: "38px",
        fontSize: "0.875rem",
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    }),
    [fieldInvalid.city],
  );

  return (
    <ExplorerWizardShell
      step={1}
      totalSteps={FLOW_TOTAL_STEPS}
      progressLabel={t("explorer.signup.tellUs")}
      onBack={handleBack}
      showBack
      onNext={handleNext}
      nextLabel={t("auth.continue")}
      nextDisabled={resolvingEventCode || emailExists === true || emailExistsChecking}
      loading={loading || emailExistsChecking}
      error={error}
    >
      <>
        <h1 className="explorer-wizard-title mb-3">Step 1: {t("explorer.signup.tellUs")}</h1>
        <div className="explorer-wizard-form-panel">
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("auth.firstName")}
              </label>
              <input
                className={`form-control${fieldInvalid.firstName ? " is-invalid" : ""}`}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                placeholder={t("explorer.signup.firstNamePlaceholder")}
                aria-invalid={fieldInvalid.firstName}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("auth.lastName")}
              </label>
              <input
                className={`form-control${fieldInvalid.lastName ? " is-invalid" : ""}`}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                placeholder={t("explorer.signup.lastNamePlaceholder")}
                aria-invalid={fieldInvalid.lastName}
              />
            </div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("auth.email")}
              </label>
              <input
                className={`form-control${
                  fieldInvalid.email || emailExists === true ? " is-invalid" : ""
                }`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder={t("explorer.signup.emailPlaceholder")}
                aria-invalid={fieldInvalid.email || emailExists === true}
              />
              {emailExistsChecking ? (
                <div className="form-text">{t("auth.checkingEmail")}</div>
              ) : null}
              {emailExists === true ? (
                <div className="invalid-feedback d-block">
                  {t("auth.emailAlreadyExistsLogin")}{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    onClick={goToLoginForExistingEmail}
                  >
                    {t("auth.logIn")}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("explorer.signup.number")}
              </label>
              <div
                className={`form-control p-0 explorer-wizard-phone-field${
                  fieldInvalid.phone ? " is-invalid" : ""
                }`}
              >
                <PhoneInput
                  international
                  defaultCountry="GB"
                  value={phoneNumber || undefined}
                  onChange={(value) => setPhoneNumber(value ?? "")}
                  placeholder={t("explorer.signup.phonePlaceholder")}
                  className="phone-input w-100"
                  aria-invalid={fieldInvalid.phone}
                />
              </div>
            </div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("explorer.signup.cityCountry")}
              </label>
              <AsyncSelect<CityOption, false, GroupBase<CityOption>>
                instanceId="explorer-signup-city"
                inputId="explorer-signup-city-input"
                aria-label={t("explorer.signup.cityCountry")}
                loadOptions={(inputValue, callback) => {
                  loadCityOptions(inputValue, callback);
                }}
                placeholder={t("explorer.signup.cityPlaceholder")}
                isClearable
                styles={citySelectStyles}
                classNamePrefix="react-select"
                aria-invalid={fieldInvalid.city}
                value={
                  city
                    ? {
                        value: city,
                        label: city,
                        city: "",
                        country: "",
                      }
                    : null
                }
                onChange={(selected: CityOption | null) => {
                  setCity(
                    selected
                      ? `${selected.city}, ${selected.country}`.trim()
                      : "",
                  );
                }}
                defaultOptions={false}
                noOptionsMessage={({ inputValue }) => {
                  if (!inputValue || inputValue.trim().length === 0) {
                    return t("explorer.signup.startTypingCities");
                  }
                  return t("explorer.signup.noCitiesFound");
                }}
                loadingMessage={() => t("explorer.signup.searchingCities")}
                cacheOptions
                isSearchable
                filterOption={null}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("explorer.signup.jobTitle")}
              </label>
              <input
                className={`form-control${fieldInvalid.jobTitle ? " is-invalid" : ""}`}
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                autoComplete="organization-title"
                placeholder={t("explorer.signup.jobTitlePlaceholder")}
                aria-invalid={fieldInvalid.jobTitle}
              />
            </div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("explorer.signup.companyName")}
              </label>
              <input
                className={`form-control${fieldInvalid.companyName ? " is-invalid" : ""}`}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
                placeholder={t("explorer.signup.companyPlaceholder")}
                aria-invalid={fieldInvalid.companyName}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold text-secondary">
                {t("explorer.signup.sector")}
              </label>
              <select
                className={`form-select${fieldInvalid.sector ? " is-invalid" : ""}`}
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                aria-invalid={fieldInvalid.sector}
              >
                <option value="">{t("explorer.signup.selectSector")}</option>
                {SECTOR_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-top border-secondary border-opacity-25 pt-3 mt-1">
            <p className="small fw-semibold text-secondary mb-2">
              {t("explorer.signup.consents")}
            </p>
            <div
              className={`explorer-wizard-consent-row form-check mb-3${
                fieldInvalid.gdprConsent ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                className={`form-check-input${fieldInvalid.gdprConsent ? " is-invalid" : ""}`}
                type="checkbox"
                id="explorer-consent-gdpr"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                aria-invalid={fieldInvalid.gdprConsent}
              />
              <label
                className="form-check-label small text-secondary"
                htmlFor="explorer-consent-gdpr"
              >
                <span className="fw-semibold text-dark">{t("explorer.signup.gdprTitle")} </span>
                {t("explorer.signup.gdprText")}{" "}
                <Link
                  to="/members-benefits-smbf"
                  className="link-primary text-decoration-underline"
                >
                  {t("explorer.signup.termsInfo")}
                </Link>
                . {t("explorer.signup.privacyQuestions")}{" "}
                <Link
                  to="/contact-us"
                  className="link-primary text-decoration-underline"
                >
                  {t("footer.contactUs")}
                </Link>
                .
              </label>
            </div>
            <div
              className={`explorer-wizard-consent-row form-check mb-0${
                fieldInvalid.marketingConsent ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                className={`form-check-input${fieldInvalid.marketingConsent ? " is-invalid" : ""}`}
                type="checkbox"
                id="explorer-consent-marketing"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                aria-invalid={fieldInvalid.marketingConsent}
              />
              <label
                className="form-check-label small text-secondary"
                htmlFor="explorer-consent-marketing"
              >
                <span className="fw-semibold text-dark">{t("explorer.signup.marketingTitle")} </span>
                {t("explorer.signup.marketingText")}
              </label>
            </div>
          </div>
        </div>
      </>
    </ExplorerWizardShell>
  );
}

export default ExplorerSignup;
